import { useCallback, useEffect, useState } from 'react';
import { Building2, CalendarClock, Loader2, Users } from 'lucide-react';
import { useToast } from '@foundation/hooks/use-toast';
import { useUser } from '@foundation/contextapi/UserContext';
import { getActiveWorkspaceId } from '../../../../header/activeWorkspace';
import {
  fetchAdminSignups,
  type DocumentRegisterSignup,
  type SignupStats,
} from '../../services/documentRegisterSignupService';

export const DocumentRegisterSignupsPage = () => {
  const { toast } = useToast();
  const { user } = useUser();
  const [items, setItems] = useState<DocumentRegisterSignup[]>([]);
  const [stats, setStats] = useState<SignupStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setIsLoading(true);
    try {
      const orgId = getActiveWorkspaceId() ?? undefined;
      const data = await fetchAdminSignups({ limit: 200 }, user.id, orgId);
      setItems(data.items);
      setStats(data.stats);
      setForbidden(false);
    } catch (e) {
      if (e instanceof Error && e.message.includes('403')) setForbidden(true);
      else toast({ title: 'Error', description: 'Failed to load signups', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, toast]);

  useEffect(() => { void load(); }, [load]);

  if (forbidden) {
    return <div className="p-10 text-center text-muted-foreground">Admin access required.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Document Register Signups</h1>
        <p className="text-sm text-muted-foreground">
          Waitlist signups from the Document Register "Notify me" form, with the submitter's profile and organisation.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="rounded-xl border border-border p-4">
            <div className="text-xs text-muted-foreground">Total signups</div>
            <div className="text-3xl font-bold flex items-center gap-2">
              {stats.totalCount}
              <Users className="h-5 w-5 text-[#33b864]" />
            </div>
          </div>
          <div className="rounded-xl border border-border p-4">
            <div className="text-xs text-muted-foreground">Organisations</div>
            <div className="text-3xl font-bold flex items-center gap-2">
              {stats.uniqueOrgs}
              <Building2 className="h-5 w-5 text-[#33b864]" />
            </div>
          </div>
          <div className="rounded-xl border border-border p-4">
            <div className="text-xs text-muted-foreground">Last 7 days</div>
            <div className="text-3xl font-bold flex items-center gap-2">
              {stats.last7Days}
              <CalendarClock className="h-5 w-5 text-[#33b864]" />
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center text-muted-foreground">No signups yet.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-border bg-muted/40">
                <th className="p-3">Email</th>
                <th className="p-3">Name</th>
                <th className="p-3">Organisation</th>
                <th className="p-3">Role</th>
                <th className="p-3">Job title</th>
                <th className="p-3">Department</th>
                <th className="p-3">Phone</th>
                <th className="p-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-border last:border-0 align-top">
                  <td className="p-3 whitespace-nowrap font-medium">
                    {it.email}
                    {it.account_email && it.account_email !== it.email && (
                      <span className="block text-xs text-muted-foreground">account: {it.account_email}</span>
                    )}
                  </td>
                  <td className="p-3 whitespace-nowrap">{it.full_name ?? '—'}</td>
                  <td className="p-3 whitespace-nowrap">{it.org_name ?? '—'}</td>
                  <td className="p-3 whitespace-nowrap capitalize">{it.org_role ?? '—'}</td>
                  <td className="p-3 whitespace-nowrap">{it.job_title ?? '—'}</td>
                  <td className="p-3 whitespace-nowrap">{it.department ?? '—'}</td>
                  <td className="p-3 whitespace-nowrap">{it.phone ?? '—'}</td>
                  <td className="p-3 whitespace-nowrap">
                    {new Date(it.created_at).toLocaleDateString()}{' '}
                    <span className="text-xs text-muted-foreground">
                      {new Date(it.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
